import type { WorkflowCustomField } from '../../types';
import { shouldShowConditionalField } from '../../utils/conditionalField';
import { serializeCheckFieldValue } from '../../utils/purchaseCustomFieldValues';
import { hasTableContent, serializeTableValue } from '../../utils/tableCustomField';

export type IncidentCustomFieldValues = Record<number, unknown>;
export type IncidentCustomFieldFiles = Record<number, File | File[] | null>;

export type IncidentCustomFieldPayloadItem = {
  custom_field_id: number;
  text_value: string;
};

const FILE_TYPES = new Set(['file', 'files']);

export function isIncidentFieldRequired(field: WorkflowCustomField): boolean {
  if (field.step_is_required !== undefined && field.step_is_required !== null) {
    return !!field.step_is_required;
  }
  return !!field.is_required;
}

export function isIncidentFieldVisible(field: WorkflowCustomField): boolean {
  return field.step_is_visible === undefined ? true : !!field.step_is_visible;
}

export function visibleIncidentFields(
  fields: WorkflowCustomField[],
  values: IncidentCustomFieldValues,
): WorkflowCustomField[] {
  return fields.filter(
    (field) =>
      isIncidentFieldVisible(field) && shouldShowConditionalField(field, fields, values),
  );
}

function isEmptyValue(field: WorkflowCustomField, value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (field.field_type === 'table') return !hasTableContent(value);
  if (field.field_type === 'period') {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      if (parsed && typeof parsed === 'object') {
        return !String(parsed.start || '').trim() && !String(parsed.end || '').trim();
      }
    } catch {
      return true;
    }
  }
  if (field.field_type === 'currency') {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      if (parsed && typeof parsed === 'object') {
        return parsed.value === undefined || parsed.value === null || String(parsed.value).trim() === '';
      }
    } catch {
      return true;
    }
  }
  return false;
}

export function serializeIncidentCustomFields(
  fields: WorkflowCustomField[],
  values: IncidentCustomFieldValues,
): IncidentCustomFieldPayloadItem[] {
  const payload: IncidentCustomFieldPayloadItem[] = [];
  for (const field of visibleIncidentFields(fields, values)) {
    if (FILE_TYPES.has(field.field_type) || field.field_type === 'calculation') continue;
    const value = values[field.id];
    if (isEmptyValue(field, value)) continue;
    let text = '';
    if (field.field_type === 'table') {
      text = serializeTableValue(value);
    } else if (field.field_type === 'check') {
      text = serializeCheckFieldValue(field, value) || '';
    } else if (field.field_type === 'list' || field.field_type === 'combo') {
      text = field.allow_multiple && Array.isArray(value) ? JSON.stringify(value) : String(value);
    } else if (field.field_type === 'currency' || field.field_type === 'period') {
      text = typeof value === 'string' ? value : JSON.stringify(value);
    } else {
      text = typeof value === 'string' ? value : JSON.stringify(value);
    }
    if (!text.trim()) continue;
    payload.push({ custom_field_id: field.id, text_value: text });
  }
  return payload;
}

export function incidentFileFieldIds(
  fields: WorkflowCustomField[],
  files: IncidentCustomFieldFiles,
  values: IncidentCustomFieldValues,
): number[] {
  return visibleIncidentFields(fields, values)
    .filter((field) => FILE_TYPES.has(field.field_type) && hasIncidentFieldFile(files[field.id]))
    .map((field) => field.id);
}

export async function uploadIncidentCustomFieldFiles(
  ticketId: number,
  files: IncidentCustomFieldFiles,
): Promise<void> {
  const { changesApi } = await import('../../services/changesApi');
  for (const [fieldId, value] of Object.entries(files)) {
    if (!value) continue;
    const formData = new FormData();
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      value.forEach((file) => formData.append('files', file));
      await changesApi.uploadIncidentTicketCustomFieldFile(ticketId, Number(fieldId), formData, true);
    } else {
      formData.append('file', value);
      await changesApi.uploadIncidentTicketCustomFieldFile(ticketId, Number(fieldId), formData, false);
    }
  }
}

export function hasIncidentFieldFile(value: File | File[] | null | undefined): boolean {
  if (!value) return false;
  return Array.isArray(value) ? value.length > 0 : true;
}

export function validateIncidentTicketFields(
  fields: WorkflowCustomField[],
  values: IncidentCustomFieldValues,
  files: IncidentCustomFieldFiles,
): string | null {
  const missing: string[] = [];
  for (const field of visibleIncidentFields(fields, values)) {
    if (field.step_is_blocked || !isIncidentFieldRequired(field)) continue;
    const label = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
    if (FILE_TYPES.has(field.field_type)) {
      if (!hasIncidentFieldFile(files[field.id])) missing.push(label);
      continue;
    }
    if (isEmptyValue(field, values[field.id])) missing.push(label);
  }
  if (!missing.length) return null;
  return `Preencha as informações da solicitação: ${missing.join(', ')}.`;
}

export function groupIncidentFields(fields: WorkflowCustomField[]): {
  tabs: Array<{ key: string; title: string; fields: WorkflowCustomField[] }>;
} {
  const grouped = new Map<string, WorkflowCustomField[]>();
  const ungrouped: WorkflowCustomField[] = [];
  for (const field of fields) {
    if (!isIncidentFieldVisible(field)) continue;
    const groupName = field.group_name?.trim();
    if (groupName) {
      const current = grouped.get(groupName) || [];
      current.push(field);
      grouped.set(groupName, current);
    } else {
      ungrouped.push(field);
    }
  }
  const tabs: Array<{ key: string; title: string; fields: WorkflowCustomField[] }> = [];
  grouped.forEach((groupFields, title) => {
    tabs.push({ key: title, title, fields: groupFields });
  });
  if (ungrouped.length) {
    tabs.push({
      key: 'geral',
      title: tabs.length ? 'Geral' : 'Campos Customizados',
      fields: ungrouped,
    });
  }
  return { tabs };
}

export const INCIDENT_EMPTY_TAB_HINT =
  'Preencha os campos dependentes na aba Informações para liberar os campos desta aba.';

export function hydrateIncidentCustomFieldValues(
  raw?: Record<string, unknown> | Record<number, unknown> | null,
): IncidentCustomFieldValues {
  const next: IncidentCustomFieldValues = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return next;
  Object.entries(raw).forEach(([key, value]) => {
    const id = Number(key);
    if (!Number.isInteger(id) || id <= 0) return;
    if (value === undefined || value === null || value === '') return;
    next[id] = value;
  });
  return next;
}

export function pickIncidentFieldValues(
  values: IncidentCustomFieldValues | Record<string, unknown> | null | undefined,
  fields: WorkflowCustomField[],
): IncidentCustomFieldValues {
  const allowed = new Set(fields.map((field) => field.id));
  const next: IncidentCustomFieldValues = {};
  if (!values) return next;
  Object.entries(values).forEach(([key, value]) => {
    const id = Number(key);
    if (!allowed.has(id)) return;
    if (value === undefined || value === null || value === '') return;
    next[id] = value;
  });
  return next;
}

export function serializeIncidentCustomFieldDefaults(
  fields: WorkflowCustomField[],
  values: IncidentCustomFieldValues,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  serializeIncidentCustomFields(fields, values).forEach((item) => {
    payload[String(item.custom_field_id)] = item.text_value;
  });
  return payload;
}

/** Sugere opção de Serviços a partir da fila de encaminhamento (ex.: CINFRA - InfraHosting). */
export function servicoOptionFromForwardQueueName(
  queueName: string | undefined,
  servicoField: WorkflowCustomField,
): string | undefined {
  if (!queueName?.trim()) return undefined;
  const options = splitComboOptions(servicoField.options);
  if (!options.length) return undefined;
  const normalizedQueue = queueName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const rules: Array<{ pattern: RegExp; pick: (opts: string[]) => string | undefined }> = [
    {
      pattern: /infra\s*host/i,
      pick: (opts) => opts.find((o) => /infra\s*host/i.test(o)) || 'Infrahosting',
    },
    {
      pattern: /infra\s*local/i,
      pick: (opts) =>
        opts.find((o) => /infraestrutura\s*local/i.test(o)) || 'Infraestrutura Local',
    },
    { pattern: /oracle/i, pick: (opts) => opts.find((o) => /oracle/i.test(o)) || 'Banco Oracle' },
    { pattern: /nuvem/i, pick: (opts) => opts.find((o) => /^nuvens$/i.test(o)) || 'Nuvens' },
    {
      pattern: /vulner/i,
      pick: (opts) => opts.find((o) => /vulner/i.test(o)) || 'Vulnerabilidades',
    },
  ];
  for (const rule of rules) {
    if (!rule.pattern.test(normalizedQueue)) continue;
    const hit = rule.pick(options);
    if (hit && options.includes(hit)) return hit;
    return options.find((o) => o.toLowerCase() === String(hit || '').toLowerCase());
  }
  return undefined;
}

export function findServicosField(fields: WorkflowCustomField[]): WorkflowCustomField | undefined {
  return fields.find((field) => {
    const label = (field.label || field.name || '').toLowerCase();
    return label.includes('servi');
  });
}

export function splitComboOptions(raw?: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    /* lista separada por vírgula */
  }
  return raw
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}
